import {
    useEffect,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type GestureResponderEvent,
    type View,
    Animated,
    Platform
} from "react-native";
import type IAnimatedIconProps from "./type";
import stylesheet, {
    getAnimatedIconAnimation,
    ANIMATED_ICON_FALLBACKS,
    getAnimatedIconTrigger,
    getAnimatedIconStyle,
    useStyles
} from "./stylesheet";
import AnimatedIconParts from "./components/parts";
import AnimatedIconTransition from "./components/transition";
import {
    getAnimatedIconNodes
} from "./nodes";
import useAnimatedIcon from "./useAnimatedIcon";
import useAnimatedIconTransition from "./useAnimatedIconTransition";
import useIconHost from "./useIconHost";
import {
    getAnimatedIconPartsKind,
    getAnimatedIconPlan
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getIconMirrored,
    getIconColor,
    getIconSize
} from "../icon/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import Icon from "../icon";

const AnimatedIcon: FC<IAnimatedIconProps> = ({
    animationTrigger: animationTriggerProp,
    iconTransition: iconTransitionProp,
    animation: animationProp = "none",
    onTouchStart: onTouchStartProp,
    "aria-label": ariaLabel,
    isTransitioned = false,
    accessibilityLabel,
    animationDelay = 0,
    onTransitionEnd,
    size = "medium",
    icon: iconProp,
    onAnimationEnd,
    color = "mid",
    customColor,
    customTheme,
    isMirrored,
    fromIcon,
    toIcon,
    style,
    ...props
}) => {
    const {
        durations,
        iconSizes,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const nodeRef = useRef<View>(null);

    const icon = iconProp ?? (isTransitioned ? toIcon : fromIcon);

    const iconTransition = iconTransitionProp ?? (iconProp ? "none" : "morph");

    const animation = iconTransition === "none" ? animationProp : "none";

    const {
        transition
    } = useAnimatedIconTransition({
        onTransitionEnd,
        iconTransition,
        durations,
        icon
    });

    const currentTrigger = getAnimatedIconTrigger({
        animationTrigger: animationTriggerProp,
        animation
    });

    const nodes = useMemo(() => icon ? getAnimatedIconNodes({
        icon
    }) : null, [
        icon
    ]);

    const partsKind = nodes && icon ? getAnimatedIconPartsKind({
        trigger: currentTrigger,
        animation,
        icon
    }) : null;

    const plan = useMemo(() => nodes && partsKind && icon ? getAnimatedIconPlan({
        kind: partsKind,
        durations,
        nodes,
        icon
    }) : null, [
        durations.medium,
        durations.short,
        durations.long,
        durations.loop,
        partsKind,
        nodes,
        icon
    ]);

    const currentAnimation = getAnimatedIconAnimation({
        isParts: plan !== null,
        animation
    });

    const currentSize = getIconSize({
        iconSizes,
        size
    });

    const {
        handlePlay,
        progress
    } = useAnimatedIcon({
        delay: Math.max(animationDelay, 0),
        animation: currentAnimation,
        trigger: currentTrigger,
        onAnimationEnd,
        durations,
        plan
    });

    useIconHost({
        trigger: currentTrigger,
        onTrigger: handlePlay,
        nodeRef
    });

    const animatedStyle = useMemo(() => getAnimatedIconStyle({
        animation: currentAnimation,
        progress
    }), [
        currentAnimation,
        progress
    ]);

    const currentColor = getIconColor({
        customColor,
        colors,
        color
    });

    const isIconMirrored = (transition ? [
        transition.fromIcon,
        transition.toIcon
    ] : [
        icon
    ]).some((item) => item ? getIconMirrored({
        icon: item,
        isMirrored
    }) : false);

    const {
        container: containerDynamicStyle
    } = useStyles({
        size: currentSize
    });

    const onTouchStart = (event: GestureResponderEvent) => {
        onTouchStartProp?.(event);

        if(Platform.OS === "web" || currentTrigger !== "press") {
            return;
        }

        handlePlay();
    };

    useEffect(() => {
        if(icon) {
            return;
        }

        warnOnce({
            message: "AnimatedIcon: pass icon, or fromIcon and toIcon."
        });
    }, [
        icon
    ]);

    useEffect(() => {
        if(animationProp === "none" || animation !== "none") {
            return;
        }

        warnOnce({
            message: "AnimatedIcon: animation is ignored while iconTransition is morph or swap."
        });
    }, [
        animationProp,
        animation
    ]);

    useEffect(() => {
        if(plan || ANIMATED_ICON_FALLBACKS[animation] === animation) {
            return;
        }

        warnOnce({
            message: `AnimatedIcon: animation "${animation}" needs a Lucide icon with readable nodes and plays "${currentAnimation}" instead.`
        });
    }, [
        currentAnimation,
        animation,
        plan
    ]);

    return <Animated.View
        {...props}
        onTouchStart={onTouchStart}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            animatedStyle
        ]}
        ref={nodeRef}
    >
        {
            transition ?
                <AnimatedIconTransition
                    label={ariaLabel ?? accessibilityLabel}
                    progress={transition.progress}
                    isMirrored={isIconMirrored}
                    customTheme={customTheme}
                    plan={transition.plan}
                    key={transition.key}
                    color={currentColor}
                    size={currentSize}
                />
            :
                plan && nodes ?
                    <AnimatedIconParts
                        label={ariaLabel ?? accessibilityLabel}
                        isMirrored={isIconMirrored}
                        customTheme={customTheme}
                        color={currentColor}
                        progress={progress}
                        size={currentSize}
                        nodes={nodes}
                        plan={plan}
                    />
            :
            icon ?
                <Icon
                    accessibilityLabel={accessibilityLabel}
                    customTheme={customTheme}
                    customColor={customColor}
                    isMirrored={isMirrored}
                    aria-label={ariaLabel}
                    color={color}
                    size={size}
                    icon={icon}
                />
            :
                null
        }
    </Animated.View>;
};
export default AnimatedIcon;
